import { useCallback, useEffect, useState } from 'react';
import { setterAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Bot, RefreshCw, Loader2, Copy, Check, ExternalLink, AlertTriangle,
  MessageSquare, Clock, Send, Stethoscope, XCircle,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* Setter-Posteingang — die Arbeitsfläche für Phase 1 des WhatsApp-Setters.
 *
 * Der Setter sendet in Phase 1 selbst nichts. Er schreibt Antwortentwürfe in
 * die Aufgabenliste, und hier steht der Ablauf, der daraus eine gesendete
 * Nachricht macht: lesen, kopieren, in WhatsApp Business senden, abhaken.
 *
 * Bewusst KEIN Bearbeitungsfeld: Wer den Text ändern will, schreibt ohnehin
 * direkt in WhatsApp. Ein zweites Textfeld hier würde nur vortäuschen, dass
 * die Änderung irgendwo ankommt.
 *
 * Zwei Sorten Karten, absichtlich getrennt:
 *   Übernahme — der Setter hat gestoppt (Kündigung, Verletzung, Kind ohne
 *               Elternteil, KI-Ausfall). Da muss ein Mensch ANTWORTEN.
 *   Entwurf   — fertiger Text, nur noch senden.
 * Übernahmen stehen oben, weil sie eine echte Antwort brauchen.
 */

const GRUND_TEXT = {
  phase1: 'Entwurfsmodus',
  setter_aus: 'Setter ausgeschaltet',
  nachtruhe: 'außerhalb der Sendezeit',
  versandfehler: 'Versand fehlgeschlagen',
  kein_kanal: 'kein WhatsApp-Kanal verbunden',
};

function alterText(iso) {
  if (!iso) return '';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  if (min < 60 * 24) return `vor ${Math.floor(min / 60)} h`;
  return `vor ${Math.floor(min / (60 * 24))} Tagen`;
}

export default function SetterPosteingangPage() {
  const [aufgaben, setAufgaben] = useState([]);
  const [laedt, setLaedt] = useState(true);
  const [busy, setBusy] = useState(null);
  const [kopiert, setKopiert] = useState(null);
  const [test, setTest] = useState(null);      // Ergebnis des KI-Selbsttests
  const [testLaeuft, setTestLaeuft] = useState(false);

  const laden = useCallback(async () => {
    try {
      const { data } = await setterAPI.posteingang();
      setAufgaben(data.aufgaben || []);
    } catch (e) {
      toast.error('Posteingang konnte nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  const selbsttest = async () => {
    setTestLaeuft(true);
    setTest(null);
    try {
      const { data } = await setterAPI.selbsttest();
      setTest(data);
    } catch (e) {
      setTest({ ok: false, stufe: 'aufruf', diagnose:
        'Der Selbsttest selbst ist gescheitert — vermutlich fehlt die Berechtigung '
        + 'oder der Server antwortet nicht.',
        fehler: e?.response?.data?.detail || e?.message });
    } finally {
      setTestLaeuft(false);
    }
  };

  const kopieren = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setKopiert(id);
      setTimeout(() => setKopiert((k) => (k === id ? null : k)), 2000);
    } catch {
      // Clipboard braucht einen sicheren Kontext. Statt still zu scheitern:
      // sagen, was zu tun ist.
      toast.error('Kopieren nicht möglich — Text bitte markieren und kopieren');
    }
  };

  const abhaken = async (id) => {
    setBusy(id);
    try {
      await setterAPI.erledigt(id);
      setAufgaben((a) => a.filter((x) => x._id !== id));
    } catch (e) {
      toast.error(e?.response?.status === 409
        ? 'War schon erledigt' : 'Konnte nicht abgehakt werden');
      laden();
    } finally {
      setBusy(null);
    }
  };

  const uebernahmen = aufgaben.filter((a) => a.art === 'setter_uebernahme');
  const entwuerfe = aufgaben.filter((a) => a.art === 'setter_entwurf');

  if (laedt) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Bot className="w-6 h-6 text-primary" /> Setter-Posteingang
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Entwürfe prüfen, in WhatsApp Business senden, abhaken.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={selbsttest} disabled={testLaeuft}>
            {testLaeuft
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Stethoscope className="w-4 h-4 mr-1.5" />}
            KI-Selbsttest
          </Button>
          <Button variant="outline" size="sm" onClick={laden}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Aktualisieren
          </Button>
        </div>
      </div>

      {test && <TestErgebnis test={test} onSchliessen={() => setTest(null)} />}

      {aufgaben.length === 0 && (
        <Card>
          <CardContent className="py-14 text-center text-muted-foreground">
            <MessageSquare className="w-8 h-8 mx-auto mb-3 opacity-40" />
            <p className="font-medium text-foreground">Nichts offen</p>
            <p className="text-sm mt-1">
              Sobald jemand über WhatsApp schreibt, liegt hier ein Entwurf.
            </p>
          </CardContent>
        </Card>
      )}

      {uebernahmen.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-red-700 flex items-center gap-1.5">
            <AlertTriangle className="w-4 h-4" />
            Du musst antworten ({uebernahmen.length})
          </h2>
          {uebernahmen.map((a) => (
            <Karte key={a._id} aufgabe={a} dringend
                   busy={busy === a._id} kopiert={kopiert === a._id}
                   onKopieren={kopieren} onAbhaken={abhaken} />
          ))}
        </section>
      )}

      {entwuerfe.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5">
            <Send className="w-4 h-4" /> Zum Senden ({entwuerfe.length})
          </h2>
          {entwuerfe.map((a) => (
            <Karte key={a._id} aufgabe={a}
                   busy={busy === a._id} kopiert={kopiert === a._id}
                   onKopieren={kopieren} onAbhaken={abhaken} />
          ))}
        </section>
      )}
    </div>
  );
}

function TestErgebnis({ test, onSchliessen }) {
  /* Zeigt, ob die KI wirklich erreichbar ist. Ohne das sieht ein Entwurf aus
   * einem Modellausfall genauso aus wie ein echter — der Setter fällt bewusst
   * still auf eine freundliche Standardantwort zurück. */
  const ok = test.ok;
  return (
    <Card className={ok ? 'border-emerald-300' : 'border-amber-400'}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 font-medium">
            {ok
              ? <Check className="w-4 h-4 text-emerald-600" />
              : <XCircle className="w-4 h-4 text-amber-600" />}
            {ok ? 'KI antwortet' : 'KI antwortet nicht wie erwartet'}
          </div>
          <Button size="sm" variant="ghost" onClick={onSchliessen}>Schließen</Button>
        </div>

        <p className="text-sm text-muted-foreground">{test.diagnose}</p>

        {test.antwort && (
          <div className="rounded-md bg-muted/60 p-3 text-sm whitespace-pre-wrap">
            {test.antwort}
          </div>
        )}
        {test.felder_erkannt && Object.keys(test.felder_erkannt).length > 0 && (
          <p className="text-xs text-muted-foreground">
            Erkannt: {Object.entries(test.felder_erkannt)
              .map(([k, v]) => `${k}=${v}`).join(', ')}
          </p>
        )}
        {test.fehler && (
          <pre className="text-xs bg-muted/60 rounded p-2 overflow-x-auto whitespace-pre-wrap">
            {test.fehler}
          </pre>
        )}
        {test.rohantwort && !ok && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Rohantwort des Modells
            </summary>
            <pre className="mt-1 bg-muted/60 rounded p-2 overflow-x-auto whitespace-pre-wrap">
              {test.rohantwort}
            </pre>
          </details>
        )}
        {test.modell && (
          <p className="text-xs text-muted-foreground">
            Modell: {test.modell} · Modus: {test.modus}
            {test.setter_aktiv === false && ' · Setter ist ausgeschaltet'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Karte({ aufgabe, dringend, busy, kopiert, onKopieren, onAbhaken }) {
  const k = aufgabe.kontext || {};
  const text = aufgabe.vorlage || '';
  const waLink = k.whatsapp_link
    || (aufgabe.telefon ? `https://wa.me/${String(aufgabe.telefon).replace(/[^0-9]/g, '')}` : null);

  return (
    <Card className={dringend ? 'border-red-300' : undefined}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="font-medium truncate">{k.name || 'Neue Anfrage'}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
              {aufgabe.telefon && <span>{aufgabe.telefon}</span>}
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {alterText(aufgabe.erstellt_am)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {dringend && aufgabe.hinweis && (
              <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                {aufgabe.hinweis}
              </Badge>
            )}
            {!dringend && GRUND_TEXT[aufgabe.setter_grund] && (
              <Badge variant="secondary">{GRUND_TEXT[aufgabe.setter_grund]}</Badge>
            )}
          </div>
        </div>

        {text && (
          <div className="rounded-md bg-muted/60 p-3 text-sm whitespace-pre-wrap leading-relaxed">
            {text}
          </div>
        )}

        {Array.isArray(k.offene_felder) && k.offene_felder.length > 0 && (
          <p className="text-xs text-muted-foreground">
            Noch offen: {k.offene_felder.join(', ')}
          </p>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {text && (
            <Button size="sm" variant="outline"
                    onClick={() => onKopieren(aufgabe._id, text)}>
              {kopiert
                ? <><Check className="w-4 h-4 mr-1.5 text-emerald-600" /> Kopiert</>
                : <><Copy className="w-4 h-4 mr-1.5" /> Text kopieren</>}
            </Button>
          )}
          {waLink && (
            <Button size="sm" variant="outline" asChild>
              <a href={waLink} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-1.5" /> In WhatsApp öffnen
              </a>
            </Button>
          )}
          <Button size="sm" disabled={busy}
                  onClick={() => onAbhaken(aufgabe._id)}>
            {busy
              ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              : <Check className="w-4 h-4 mr-1.5" />}
            {dringend ? 'Erledigt' : 'Gesendet'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
